import React, { useState, lazy, Suspense, useEffect } from "react";
import { useAuth } from "../../hooks/AuthenticationProvider";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  doc,
  getDoc,
  updateDoc,
  increment,
  collection,
  getDocs,
  orderBy,
  limit,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";
import PvPLobby from "../../components/PvPLobby";
import { db } from "../../utils/firebase";
import Spinner from "../../components/Spinner";
import PlayerStatsModal from "../../components/PlayerStatsModal";
import {
  Brain,
  Zap,
  Clock,
  Award,
  Shield,
  Sparkles,
  ChevronRight,
  Search,
  Info,
  ArrowRight,
  Target,
  BarChart2,
  Users,
  X,
  Compass,
  Activity,
} from "lucide-react";
import FullLeaderboardModal from "../../components/FullLeaderboardModal";
import MultiplayerGame from "../../components/MultiplayerGame";
import MemoryMatrixMulti from "../games/MemoryMatrixMulti.jsx";
import ColorClashMulti from "../games/ColorClashMulti.jsx";
import DataStreamMulti from "../games/DataStreamMulti.jsx";
import FocusFinderMulti from "../games/FocusFinderMulti.jsx";
import MathRaceMulti from "../games/MathRaceMulti.jsx";
import RuleShifterMulti from "../games/RuleShifterMulti.jsx";
import StopSignalMulti from "../games/StopSignalMulti.jsx";
import VisualSearchMulti from "../games/VisualSearchMulti.jsx";
import SpanTaskMulti from "../games/SpanTaskMulti.jsx";
import OldSchoolTetris from "../games/OldSchoolTetris.jsx";
import VisualSearchTask from "../games/VisualSearchTask.jsx";
import InspectionTimeMulti from "../games/InspectionTimeMulti.jsx";

const categories = [
  {
    id: "impulse-control",
    name: "Impulse Control",
    icon: Shield,
    color: "from-purple-500 to-indigo-600",
    textColor: "text-purple-300",
    description:
      "Strengthen your ability to resist triggers and regulate responses",
  },
  {
    id: "working-memory",
    name: "Working Memory",
    icon: Brain,
    color: "from-blue-500 to-cyan-600",
    textColor: "text-blue-300",
    description: "Enhance your ability to hold and manipulate information",
  },
  {
    id: "cognitive-flexibility",
    name: "Cognitive Flexibility",
    icon: Compass,
    color: "from-emerald-500 to-teal-600",
    textColor: "text-emerald-300",
    description:
      "Improve your ability to adapt thinking and switch between tasks",
  },
  {
    id: "attention",
    name: "Attention",
    icon: Target,
    color: "from-amber-500 to-orange-600",
    textColor: "text-amber-300",
    description: "Focus better and filter out distractions",
  },
  {
    id: "stress-regulation",
    name: "Stress Regulation",
    icon: Activity,
    color: "from-rose-500 to-pink-600",
    textColor: "text-rose-300",
    description: "Build resilience and improve emotional regulation",
  },
  {
    id: "processing-speed",
    name: "Processing Speed",
    icon: Zap,
    color: "from-red-500 to-orange-600",
    textColor: "text-red-300",
    description: "Increase the speed at which you process information",
  },
  {
    id: "random-games",
    name: "Random Games",
    icon: Sparkles,
    color: "from-yellow-400 to-orange-500",
    textColor: "text-yellow-300",
    description: "Explore unique and fun cognitive challenges",
  },
];

// Sample games data structure
const gamesData = [
  {
    id: "stop-signal",
    name: "Stop Signal Challenge",
    category: "impulse-control",
    thumbnail: "/assets/games/stop-signal.jpg",
    description:
      "Respond quickly to visual cues but inhibit your response when the stop signal appears.",
    benefits: [
      "Improves impulse control",
      "Enhances response inhibition",
      "Trains frontal lobe function",
    ],
    difficultyLevel: 3,
    timeToComplete: "5-10 min",
    popularity: 92,
    isNew: false,
  },
  {
    id: "soduko-challenge",
    name: "Soduko Challenge",
    category: "random-games",
    thumbnail: "/assets/games/soduko.jpg", // Add this image to public/assets/games/
    description:
      "Solve tactical soduko puzzles to train logic, planning, and pattern recognition.",
    benefits: [
      "Boosts strategic thinking",
      "Improves pattern recognition",
      "Enhances problem-solving under pressure",
    ],
    difficultyLevel: 3,
    timeToComplete: "5-10 min",
    popularity: 90,
    isNew: true,
  },
  {
    id: "snake-challenge",
    name: "Snake Challenge",
    category: "random-games",
    thumbnail: "/assets/games/snake.jpg", // Add this image to public/assets/games/
    description:
      "Solve tactical snake puzzles to train logic, planning, and pattern recognition.",
    benefits: [
      "Boosts strategic thinking",
      "Improves pattern recognition",
      "Enhances problem-solving under pressure",
    ],
    difficultyLevel: 3,
    timeToComplete: "5-10 min",
    popularity: 90,
    isNew: true,
  },
  {
    id: "tetris-challenge",
    name: "Tetris Challenge",
    category: "random-games",
    thumbnail: "/assets/games/tetris.jpg",
    description:
      "Solve tactical tetris puzzles to train logic, planning, and pattern recognition.",
    benefits: [
      "Boosts strategic thinking",
      "Improves pattern recognition",
      "Enhances problem-solving under pressure",
    ],
    difficultyLevel: 3,
    timeToComplete: "5-10 min",
    popularity: 90,
    isNew: true,
  },
  {
    id: "minesweeper-challenge",
    name: "Minesweeper",
    category: "random-games",
    thumbnail: "/assets/games/mines.jpg",
    description:
      "Solve tactical minesweeper puzzles to train logic, planning, and pattern recognition.",
    benefits: [
      "Boosts strategic thinking",
      "Improves pattern recognition",
      "Enhances problem-solving under pressure",
    ],
    difficultyLevel: 3,
    timeToComplete: "5-10 min",
    popularity: 90,
    isNew: true,
  },
  {
    id: "color-clash",
    name: "Color Clash Challenge",
    category: "impulse-control",
    thumbnail: "/assets/games/stroop.jpg",
    description: "Respond quickly to visual cues that may be a bit tricky!",
    benefits: [
      "Improves impulse control",
      "Enhances response inhibition",
      "Trains frontal lobe function",
    ],
    difficultyLevel: 3,
    timeToComplete: "5-10 min",
    popularity: 92,
    isNew: false,
  },
  {
    id: "nback",
    name: "Memory Matrix",
    category: "working-memory",
    thumbnail: "/assets/games/nback.jpg",
    description:
      "Remember and match patterns that appeared n steps earlier in the sequence.",
    benefits: [
      "Enhances working memory",
      "Improves fluid intelligence",
      "Strengthens attention",
    ],
    difficultyLevel: 4,
    timeToComplete: "10-15 min",
    popularity: 95,
    isNew: false,
  },
  {
    id: "span-task",
    name: "Memory Span",
    category: "working-memory",
    thumbnail: "/assets/games/simon.jpg",
    description:
      "Recall increasingly complex sequences while handling distractions.",
    benefits: [
      "Builds working memory capacity",
      "Improves multitasking",
      "Enhances focus",
    ],
    difficultyLevel: 3,
    timeToComplete: "7-10 min",
    popularity: 88,
    isNew: false,
  },
  {
    id: "set-shifting",
    name: "Rule Shifter",
    category: "cognitive-flexibility",
    thumbnail: "/assets/games/rule-shifter.jpg",
    description: "Adapt quickly to changing rules and patterns as they evolve.",
    benefits: [
      "Enhances mental flexibility",
      "Improves adaptation skills",
      "Reduces rigidity",
    ],
    difficultyLevel: 4,
    timeToComplete: "8-12 min",
    popularity: 87,
    isNew: false,
  },
  {
    id: "category-fluency",
    name: "Category Fluency",
    category: "cognitive-flexibility",
    thumbnail: "/assets/games/categories.jpg",
    description: "Adapt quickly to changing rules and patterns as they evolve.",
    benefits: [
      "Enhances mental flexibility",
      "Improves adaptation skills",
      "Reduces rigidity",
    ],
    difficultyLevel: 4,
    timeToComplete: "8-12 min",
    popularity: 87,
    isNew: false,
  },
  {
    id: "visual-search",
    name: "Focus Finder",
    category: "attention",
    thumbnail: "/assets/games/x.jpg",
    description:
      "Quickly locate specific targets among a field of distractors.",
    benefits: [
      "Sharpens visual attention",
      "Improves search efficiency",
      "Enhances concentration",
    ],
    difficultyLevel: 2,
    timeToComplete: "5-8 min",
    popularity: 91,
    isNew: false,
  },
  {
    id: "search-task",
    name: "Visual Search Task",
    category: "attention",
    thumbnail: "/assets/games/visualsearch.jpg",
    description:
      "Quickly locate specific targets among a field of distractors.",
    benefits: [
      "Sharpens visual attention",
      "Improves search efficiency",
      "Enhances concentration",
    ],
    difficultyLevel: 2,
    timeToComplete: "5-8 min",
    popularity: 91,
    isNew: false,
  },
  {
    id: "heart-coherence",
    name: "Breath Pacer",
    category: "stress-regulation",
    thumbnail: "/assets/games/breathwork.jpg",
    description:
      "Synchronize your breathing to optimal patterns for stress reduction and emotional control.",
    benefits: [
      "Improves HRV",
      "Reduces anxiety",
      "Enhances emotional regulation",
    ],
    difficultyLevel: 1,
    timeToComplete: "5-10 min",
    popularity: 94,
    isNew: false,
  },
  {
    id: "math-race",
    name: "Math Race",
    category: "stress-regulation",
    thumbnail: "/assets/games/math.jpg",
    description:
      "Gradually build resilience to stressors through progressive challenges.",
    benefits: [
      "Builds stress resilience",
      "Improves coping skills",
      "Reduces reactivity",
    ],
    difficultyLevel: 3,
    timeToComplete: "5 min",
    popularity: 89,
    isNew: true,
  },
  {
    id: "reaction-time",
    name: "Speed Reactor",
    category: "processing-speed",
    thumbnail: "/assets/games/reaction.jpg",
    description: "Test and improve your reaction time to various stimuli.",
    benefits: [
      "Accelerates neural processing",
      "Improves reaction time",
      "Enhances coordination",
    ],
    difficultyLevel: 2,
    timeToComplete: "3-5 min",
    popularity: 93,
    isNew: false,
  },
  {
    id: "inspection-time",
    name: "Inspection Time",
    category: "processing-speed",
    thumbnail: "/assets/games/squares.jpg",
    description: "Test and improve your reaction time to various stimuli.",
    benefits: [
      "Accelerates neural processing",
      "Improves reaction time",
      "Enhances coordination",
    ],
    difficultyLevel: 2,
    timeToComplete: "3-5 min",
    popularity: 93,
    isNew: false,
  },
  {
    id: "information-processing",
    name: "Data Stream",
    category: "processing-speed",
    thumbnail: "/assets/games/game.jpg",
    description: "Process and respond to rapidly changing information streams.",
    benefits: [
      "Speeds up information processing",
      "Improves decision speed",
      "Enhances mental agility",
    ],
    difficultyLevel: 4,
    timeToComplete: "8-10 min",
    popularity: 86,
    isNew: true,
  },
];

const gameRoutes = {
  nback: "/memorymatrix",
  "span-task": "/spantask",
  "stop-signal": "/stopsignal",
  "reaction-time": "/speedreactor",
  "set-shifting": "/ruleshifter",
  "visual-search": "/focusfinder",
  "heart-coherence": "/breathpacer",
  "information-processing": "/datastream",
  "inspection-time": "/inspectiontime",
  "math-race": "/mathrace",
  "color-clash": "/colorclash",
  "category-fluency": "/categoryfluency",
  "soduko-challenge": "/sudoku",
  "snake-challenge": "/snake",
  "search-task": "/visualsearchtask",
  "tetris-challenge": "/tetris",
  "minesweeper-challenge": "/minesweeper",
};

const GameCard = ({ game, openMultiplayerLobby }) => {
  const navigate = useNavigate();
  const category = categories.find((c) => c.id === game.category);

  // List of game IDs that should disable the multiplayer button entirely
  const disableMultiplayerFor = [
    "tetris-challenge",
    "snake-challenge",
    "soduko-challenge",
    "heart-coherence",
    "minesweeper-challenge",
  ];

  // List of games with full multiplayer support
  const fullyMultiplayerGames = [
    "color-clash",
    "information-processing",
    "math-race",
  ];

  const multiplayerAvailable = fullyMultiplayerGames.includes(game.id);

  const handleGameClick = () => {
    if (gameRoutes[game.id]) {
      navigate(gameRoutes[game.id]);
    }
  };

  const handleSinglePlayerClick = (e) => {
    e.stopPropagation();
    handleGameClick();
  };

  // In Gameroom.jsx, update the handleMultiplayerClick function in GameCard:
  const handleMultiplayerClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const gameToRouteMap = {
      "color-clash": "/colormulti",
      "stop-signal": "/stopsignalmulti",
      "information-processing": "/datastreammulti",
      "math-race": "/mathracemulti",
      nback: "/memorymatrixmulti",
      "visual-search": "/focusfindermulti",
      "set-shifting": "/ruleshiftermulti",
      "span-task": "/spantaskmulti",
      "inspection-time": "/inspectiontimemulti",
    };

    const route = gameToRouteMap[game.id];

    // Navigate if a route exists — do nothing otherwise
    if (route) {
      navigate(`${route}?showLobby=true&game=${game.id}`);
    }
  };

  return (
    <motion.div
      onClick={(e) => {
        if (!e.defaultPrevented) handleGameClick();
      }}
      className="bg-slate-800 rounded-xl overflow-hidden border border-slate-700 hover:border-slate-600 transition-all shadow-lg cursor-pointer"
      whileHover={{ y: -5, scale: 1.02 }}
      transition={{ duration: 0.2 }}
    >
      <div className="relative">
        <div
          className={`h-40 bg-gradient-to-r ${category.color} flex items-center justify-center`}
        >
          {game.thumbnail ? (
            <img
              src={game.thumbnail}
              alt={game.name}
              className="w-full h-full object-cover opacity-80 hover:opacity-100 transition-opacity"
              onError={(e) => {
                e.target.onerror = null;
                e.target.style.display = "none";
              }}
            />
          ) : (
            <category.icon size={48} className="text-white opacity-40" />
          )}
        </div>

        {game.isNew && (
          <div className="absolute top-2 right-2 bg-emerald-500 text-white text-xs font-bold px-2 py-1 rounded-md">
            NEW
          </div>
        )}

        <div className="absolute bottom-2 right-2 bg-black/50 backdrop-blur-sm px-2 py-1 rounded-md flex items-center gap-1">
          <Clock size={12} className="text-slate-300" />
          <span className="text-xs text-white">{game.timeToComplete}</span>
        </div>
      </div>

      <div className="p-4">
        <div className="flex justify-between items-start mb-2">
          <h3 className="text-white font-medium">{game.name}</h3>
          <div
            className={`p-1.5 rounded-md ${category.textColor} bg-opacity-10 bg-slate-700`}
          >
            <category.icon size={16} className={category.textColor} />
          </div>
        </div>

        <p className="text-slate-400 text-sm mb-4 line-clamp-2">
          {game.description}
        </p>

        <div className="flex flex-col gap-2">
          <button
            onClick={handleSinglePlayerClick}
            className="bg-slate-700 hover:bg-slate-600 text-white text-sm px-3 py-1 rounded-lg flex items-center justify-center gap-1 transition-colors"
          >
            Single Player <ArrowRight size={14} />
          </button>
          {!disableMultiplayerFor.includes(game.id) && (
            <button
              onClick={handleMultiplayerClick}
              className="bg-emerald-700 hover:bg-emerald-600 text-white text-sm px-3 py-1 rounded-lg flex items-center justify-center gap-1 transition-colors"
            >
              {multiplayerAvailable ? (
                <>
                  Multiplayer <Users size={14} />
                </>
              ) : (
                <>
                  Multiplayer (Coming Soon) <Users size={14} />
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const CategoryCard = ({ category, isActive, onClick }) => {
  return (
    <motion.div
      onClick={onClick}
      className={`p-4 rounded-xl cursor-pointer transition-all border ${
        isActive
          ? `bg-gradient-to-br ${category.color} border-transparent`
          : "bg-slate-800/50 border-slate-700 hover:border-slate-600"
      }`}
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.98 }}
    >
      <div className="flex flex-col items-center text-center">
        <div
          className={`p-3 rounded-full ${
            isActive ? "bg-white/20" : `bg-slate-700 ${category.textColor}`
          } mb-3`}
        >
          <category.icon
            size={24}
            className={isActive ? "text-white" : category.textColor}
          />
        </div>
        <h3
          className={`font-medium ${
            isActive ? "text-white" : "text-slate-300"
          } mb-1`}
        >
          {category.name}
        </h3>
        <p
          className={`text-xs ${
            isActive ? "text-white/80" : "text-slate-400"
          } line-clamp-2`}
        >
          {category.description}
        </p>
      </div>
    </motion.div>
  );
};

// Animated leaderboard entry component
const LeaderboardEntry = ({ leader, rank, isEmpty = false }) => {
  if (isEmpty) {
    return (
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3, delay: rank * 0.05 }}
        className="flex items-center gap-3 py-2 px-4 rounded-lg bg-slate-800/30 border border-slate-700/50"
      >
        <div className="w-6 h-6 rounded-full flex items-center justify-center bg-slate-700/50 text-slate-400 text-xs font-bold">
          {rank + 1}
        </div>
        <div className="w-8 h-8 rounded-full overflow-hidden bg-slate-700">
          <Users size={16} className="w-full h-full p-2 text-slate-400" />
        </div>
        <div className="flex-1">
          <p className="text-sm text-slate-400 italic">Position available</p>
          <p className="text-xs text-slate-500">
            Be the first to claim this spot
          </p>
        </div>
        <div className="flex items-center gap-1">
          <Award size={14} className="text-slate-500" />
          <span className="text-slate-400 font-mono">0</span>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: rank * 0.05 }}
      className="flex items-center gap-3 py-2 px-4 rounded-lg bg-slate-800/30 border border-slate-700/50"
    >
      <div
        className={`w-6 h-6 rounded-full flex items-center justify-center ${
          rank === 0
            ? "bg-yellow-500/20 text-yellow-300"
            : rank === 1
            ? "bg-slate-400/20 text-slate-300"
            : rank === 2
            ? "bg-amber-600/20 text-amber-500"
            : "bg-slate-700/50 text-slate-400"
        } text-xs font-bold`}
      >
        {rank + 1}
      </div>

      <div className="w-8 h-8 rounded-full overflow-hidden bg-slate-700">
        {leader.avatar ? (
          <img
            src={leader.avatar}
            alt={leader.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <Users size={16} className="w-full h-full p-2 text-slate-400" />
        )}
      </div>

      <div className="flex-1">
        <p className="text-sm text-white font-medium">{leader.name}</p>
        <p className="text-xs text-slate-400">{leader.game}</p>
      </div>

      <div className="flex items-center gap-1">
        <Award size={14} className="text-purple-400" />
        <span className="text-white font-mono">
          {leader.score.toLocaleString()}
        </span>
      </div>
    </motion.div>
  );
};

// Achievements banner component
const DailyStreak = ({ days = 5, onClaim }) => {
  const maxDays = 7;

  return (
    <div className="bg-gradient-to-r from-slate-800/80 to-slate-900/80 backdrop-blur-md p-4 md:p-6 rounded-xl border border-slate-700 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-40 h-40 bg-purple-500/10 blur-3xl rounded-full -mr-20 -mt-20" />
      <div className="absolute bottom-0 left-0 w-40 h-40 bg-blue-500/10 blur-3xl rounded-full -ml-20 -mb-20" />

      <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-4 md:gap-6">
        <div>
          <h3 className="text-lg md:text-xl font-bold text-white mb-1">
            Daily Streak: {days} Days
          </h3>
          <p className="text-slate-300 text-sm md:text-base">
            Keep training your brain to unlock rewards!
          </p>
        </div>

        <div className="flex flex-col items-center md:items-end gap-3">
          <div className="flex gap-1.5">
            {Array.from({ length: maxDays }).map((_, i) => (
              <div
                key={i}
                className={`w-6 h-6 md:w-8 md:h-8 rounded-md flex items-center justify-center ${
                  i < days
                    ? "bg-gradient-to-br from-purple-500 to-blue-600 text-white"
                    : "bg-slate-800 text-slate-600"
                } relative`}
              >
                {i < days ? (
                  <Sparkles size={16} className="text-white" />
                ) : (
                  <span className="text-sm md:text-base">{i + 1}</span>
                )}
                {i === days - 1 && (
                  <motion.div
                    className="absolute inset-0 rounded-md border-2 border-white"
                    animate={{ opacity: [0.2, 0.8, 0.2] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  />
                )}
              </div>
            ))}
          </div>

          {days >= 7 ? (
            <button
              onClick={onClaim}
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-medium px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
            >
              <Sparkles size={16} />
              Claim Week Reward
            </button>
          ) : (
            <p className="text-slate-400 text-sm">
              {7 - days} more day{7 - days !== 1 ? "s" : ""} for weekly reward
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Gameroom component - properly restructured
const Gameroom = () => {
  const { organizationId, user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [leaderboard, setLeaderboard] = useState([]);
  const [games, setGames] = useState(gamesData);
  const [showIntro, setShowIntro] = useState(true);
  const [isFirstVisit, setIsFirstVisit] = useState(false);
  const [streakDays, setStreakDays] = useState(null);
  const [showFullLeaderboard, setShowFullLeaderboard] = useState(false);
  const [selectedPvPGame, setSelectedPvPGame] = useState(null);
  const [showPvPLobby, setShowPvPLobby] = useState(false);
  const [activeMatch, setActiveMatch] = useState(null);
  const [selectedGame, setSelectedGame] = useState(null);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [miniPreviewStats, setMiniPreviewStats] = useState({
    totalGames: 0,
    gameTypes: 0,
    leaderboardCount: 0,
    favoriteGame: null,
  });

  const gameTypeMap = {
    nback: "Memory Matrix",
    "span-task": "Memory Span",
    "stop-signal": "Stop Signal Challenge",
    "reaction-time": "Speed Reactor",
    "set-shifting": "Rule Shifter",
    "visual-search": "Focus Finder",
    "search-task": "Visual Search Task",
    "heart-coherence": "Breath Pacer",
    "information-processing": "Data Stream",
    "inspection-time": "Inspection Time",
    "math-race": "Math Race",
    "color-clash": "Color Clash Challenge",
    "category-fluency": "Category Fluency",
    "snake-challenge": "Snake Challenge",
    "sudoku-challenge": "Sudoku Challenge",
  };

  useEffect(() => {
    const fetchMiniStats = async () => {
      if (!organizationId || !user?.email) return;

      try {
        // Get basic game history info
        const historyRef = collection(
          db,
          `organizations/${organizationId}/users/${user.email}/gameHistory`
        );

        const historySnapshot = await getDocs(query(historyRef, limit(100)));
        const gameTypeSet = new Set();

        historySnapshot.docs.forEach((doc) => {
          const gameType = doc.data().gameType;
          if (gameType) gameTypeSet.add(gameType);
        });

        // Calculate favorite game if there are any games played
        let favoriteGame = null;
        if (historySnapshot.docs.length > 0) {
          const gameTypeCounts = {};
          historySnapshot.docs.forEach((doc) => {
            const gameType = doc.data().gameType;
            gameTypeCounts[gameType] = (gameTypeCounts[gameType] || 0) + 1;
          });

          const mostPlayedGameType = Object.entries(gameTypeCounts).sort(
            (a, b) => b[1] - a[1]
          )[0][0];

          favoriteGame = gameTypeMap[mostPlayedGameType] || mostPlayedGameType;
        }

        // Calculate leaderboard appearances (simplified - just checks if in any leaderboard)
        let leaderboardCount = 0;
        for (const gameType of gameTypeSet) {
          const leaderboardRef = doc(
            db,
            `organizations/${organizationId}/gameScores`,
            gameType
          );

          const leaderboardDoc = await getDoc(leaderboardRef);
          if (leaderboardDoc.exists()) {
            const leaderboardData = leaderboardDoc.data();

            if (leaderboardData.leaderboards) {
              for (const difficulty in leaderboardData.leaderboards) {
                const leaderboard = leaderboardData.leaderboards[difficulty];
                const inLeaderboard = leaderboard.some(
                  (entry) => entry.userId === user.email
                );
                if (inLeaderboard) {
                  leaderboardCount++;
                  break; // Count each game type only once
                }
              }
            }
          }
        }

        // Update the mini stats
        setMiniPreviewStats({
          totalGames: historySnapshot.docs.length,
          gameTypes: gameTypeSet.size,
          leaderboardCount,
          favoriteGame,
        });
      } catch (error) {
        console.error("Error fetching mini stats:", error);
      }
    };

    fetchMiniStats();
  }, [organizationId, user?.email]);

  const getGameComponentByGameId = (gameId) => {
    const gameComponents = {
      nback: MemoryMatrixMulti,
      "color-clash": ColorClashMulti,
      "information-processing": DataStreamMulti,
      "visual-search": FocusFinderMulti,
      "math-race": MathRaceMulti,
      "set-shifting": RuleShifterMulti,
    };

    // Trim gameId and check the mapping, otherwise return a fallback component
    return (
      gameComponents[String(gameId).trim()] || (() => <div>Game not found</div>)
    );
  };

  useEffect(() => {
    const checkForActiveMatches = async () => {
      if (!organizationId || !user?.email) return;

      try {
        const matchesQuery = query(
          collection(db, `organizations/${organizationId}/pvpMatches`),
          where("players", "array-contains", user.email),
          where("gameState.status", "in", ["pending", "active"])
        );

        const querySnapshot = await getDocs(matchesQuery);

        if (!querySnapshot.empty) {
          // Found active match
          const matchData = querySnapshot.docs[0].data();
          setActiveMatch(matchData.matchId);
          setSelectedGame(matchData.game);
          setShowPvPLobby(true);
        }
      } catch (error) {
        console.error("Error checking for active matches:", error);
      }
    };

    checkForActiveMatches();
  }, [organizationId, user?.email]);

  useEffect(() => {
    const updateAndRewardStreak = async () => {
      // ⏳ Delay to show loading spinner
      await new Promise((resolve) => setTimeout(resolve, 1500));

      if (!organizationId || !user?.email) {
        setIsLoading(false); // still turn loading off
        return;
      }

      const userDocRef = doc(
        db,
        `organizations/${organizationId}/users`,
        user.email
      );

      try {
        const userDocSnap = await getDoc(userDocRef);
        const today = new Date();
        const todayDate = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate()
        );

        let currentStreak = 0;
        let lastUpdate = null;
        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          currentStreak = userData.dailyStreak || 0;
          lastUpdate = userData.lastStreakUpdate
            ? userData.lastStreakUpdate.toDate()
            : null;

          setStreakDays(currentStreak);
        }

        let newStreak = currentStreak;
        let bonus = 0;

        if (!lastUpdate) {
          newStreak = 1;
          bonus = Math.floor(Math.random() * (500 - 50 + 1)) + 10;
        } else {
          const lastUpdateDate = new Date(
            lastUpdate.getFullYear(),
            lastUpdate.getMonth(),
            lastUpdate.getDate()
          );
          const diffTime = todayDate - lastUpdateDate;
          const diffDays = diffTime / (1000 * 60 * 60 * 24);

          if (diffDays === 0) {
            setIsLoading(false); // Don't reset streak, just end loading
            return;
          } else if (diffDays === 1) {
            newStreak = currentStreak + 1;
            bonus =
              newStreak < 7
                ? Math.floor(Math.random() * (500 - 50 + 1)) + 10
                : Math.floor(Math.random() * (6000 - 1000 + 1)) + 250;
            if (newStreak >= 7) newStreak = 0;
          } else {
            newStreak = 1;
            bonus = Math.floor(Math.random() * (250 - 10 + 1)) + 10;
          }
        }

        await updateDoc(userDocRef, {
          dailyStreak: newStreak,
          lastStreakUpdate: serverTimestamp(),
          gameScore: increment(bonus),
        });

        setStreakDays(newStreak);

        alert(
          `Congratulations! You've earned ${bonus} bonus points for your ${
            newStreak === 0 ? "weekly" : "daily"
          } reward!`
        );
      } catch (error) {
        console.error("Error updating daily/weekly streak:", error);
      }

      // ✅ finally show the page
      setIsLoading(false);
    };

    updateAndRewardStreak();
  }, [organizationId, user]);

  // In Gameroom.jsx
  const openMultiplayerLobby = (gameId) => {
    // navigate to the appropriate game route with a multiplayer parameter
    const gameRoute = gameRoutes[gameId];
    if (gameRoute) {
      navigate(`${gameRoute}multi?mode=multiplayer&game=${gameId}`);
    }
  };

  // Filter games based on active category and search query
  const filteredGames = games.filter(
    (game) =>
      (activeCategory === "all" || game.category === activeCategory) &&
      (searchQuery === "" ||
        game.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        game.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Simulate fetching leaderboard data from Firebase
  useEffect(() => {
    const fetchLeaderboard = async () => {
      if (!organizationId) return;

      try {
        // Reference to users collection
        const usersRef = collection(
          db,
          `organizations/${organizationId}/users`
        );

        // Create a query to get users with gameScore field, ordered by gameScore in descending order, limited to 10
        const leaderboardQuery = query(
          usersRef,
          where("gameScore", ">", 0),
          orderBy("gameScore", "desc"),
          limit(10)
        );

        const querySnapshot = await getDocs(leaderboardQuery);

        if (querySnapshot.empty) {
          // No users with scores yet
          setLeaderboard([]);
          return;
        }

        const leaderboardData = querySnapshot.docs.map((doc, index) => {
          const userData = doc.data();
          // Use email as the ID if it exists, otherwise use the document ID
          const userId = userData.email || doc.id;
          // Extract first name or use email username part
          const name = userData.firstName || userId.split("@")[0];

          // Use a random game from the games array for display purposes
          // In a real implementation, you might want to track which game gave the highest score
          const randomGameIndex = Math.floor(Math.random() * gamesData.length);
          const game = gamesData[randomGameIndex].name;

          return {
            id: userId,
            name: `${name}${
              userData.lastName ? " " + userData.lastName[0] + "." : ""
            }`,
            game: userData.lastPlayedGame || game,
            score: userData.gameScore || 0,
            avatar:
              userData.photoURL || `/assets/avatars/user${(index % 8) + 1}.jpg`,
          };
        });

        setLeaderboard(leaderboardData);
      } catch (error) {
        console.error("Error fetching leaderboard:", error);
        setLeaderboard([]);
      }
    };

    fetchLeaderboard();
  }, [organizationId]);

  // Handle claiming the streak reward
  const handleClaimReward = () => {
    // In a real implementation, this would update the user's profile, grant rewards, etc.
    alert(
      "Weekly reward claimed! You've earned 500 bonus points and unlocked a special achievement!"
    );
    setStreakDays(0);
  };

  // Show spinner until loading is complete
  if (isLoading || streakDays === null) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-slate-900 text-white">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-950 text-white p-4 md:p-6">
      {/* First-time visitor onboarding modal */}
      <AnimatePresence>
        {isFirstVisit && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-6 max-w-lg w-full border border-slate-700 shadow-xl"
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Brain size={32} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Welcome to the Recovery Gameroom
                </h2>
                <p className="text-slate-300">
                  Train your brain with games designed to strengthen neural
                  pathways affected by substance use.
                </p>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-purple-500/10 rounded-lg">
                    <Target className="text-purple-400" size={20} />
                  </div>
                  <div>
                    <h3 className="text-white font-medium">
                      Targeted Training
                    </h3>
                    <p className="text-sm text-slate-300">
                      Each game targets specific cognitive functions impaired by
                      addiction.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-500/10 rounded-lg">
                    <BarChart2 className="text-blue-400" size={20} />
                  </div>
                  <div>
                    <h3 className="text-white font-medium">
                      Track Your Progress
                    </h3>
                    <p className="text-sm text-slate-300">
                      See your improvements over time with detailed analytics.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="p-2 bg-emerald-500/10 rounded-lg">
                    <Award className="text-emerald-400" size={20} />
                  </div>
                  <div>
                    <h3 className="text-white font-medium">
                      Earn Achievements
                    </h3>
                    <p className="text-sm text-slate-300">
                      Stay motivated with rewards for consistent training.
                    </p>
                  </div>
                </div>
              </div>

              <button
                onClick={() => setIsFirstVisit(false)}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-medium py-3 rounded-xl transition-colors"
              >
                Start My Brain Training Journey
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-7xl mx-auto">
        {/* Header section */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">
              Recovery Gameroom
            </h1>
            <p className="text-slate-400">
              Train your brain, strengthen your recovery
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative flex-1 md:w-64">
              <input
                type="text"
                placeholder="Search games..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-9 pr-4 py-2 text-white placeholder:text-slate-500 focus:border-purple-500 focus:outline-none transition-colors"
              />
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
                size={16}
              />
            </div>

            <button
              onClick={() => setShowIntro(!showIntro)}
              className="p-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
              title="Game Info"
            >
              <Info size={20} />
            </button>
          </div>
        </div>

        {/* Info section - collapsible */}
        <AnimatePresence>
          {showIntro && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden mb-6"
            >
              <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-4 md:p-6">
                <div className="flex flex-col md:flex-row gap-4 md:gap-6">
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-white mb-2">
                      Brain Training for Recovery
                    </h2>
                    <p className="text-slate-300 mb-4">
                      These exercises are modeled after cognitive tasks used in
                      clinical research to improve executive function—skills
                      like working memory, attention regulation, and cognitive
                      flexibility—often disrupted by substance use.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-lg text-sm">
                        Evidence-based
                      </span>
                      <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-lg text-sm">
                        Cognitive skill-building
                      </span>

                      <span className="px-3 py-1 bg-emerald-500/20 text-emerald-300 rounded-lg text-sm">
                        Progress tracking
                      </span>
                    </div>
                  </div>

                  <div className="md:w-1/3 flex justify-center">
                    <div className="bg-slate-900/80 border border-slate-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-white font-medium text-sm">
                          Your Stats Overview
                        </h3>
                        <span className="text-xs text-slate-400">
                          At a glance
                        </span>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-xs text-slate-400">
                              Total Games Played
                            </span>
                            <span className="text-xs text-white">
                              {miniPreviewStats.totalGames}
                            </span>
                          </div>
                          <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                            <div
                              className="bg-gradient-to-r from-purple-500 to-blue-500 h-full rounded-full"
                              style={{
                                width: `${Math.min(
                                  100,
                                  (miniPreviewStats.totalGames / 25) * 100
                                )}%`,
                              }}
                            ></div>
                          </div>
                        </div>

                        <div>
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-xs text-slate-400">
                              Top 5 Rankings
                            </span>
                            <span className="text-xs text-white">
                              {miniPreviewStats.leaderboardCount}
                            </span>
                          </div>
                          <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                            <div
                              className="bg-gradient-to-r from-emerald-500 to-teal-500 h-full rounded-full"
                              style={{
                                width: `${Math.min(
                                  100,
                                  (miniPreviewStats.leaderboardCount / 10) * 100
                                )}%`,
                              }}
                            ></div>
                          </div>
                        </div>

                        {miniPreviewStats.favoriteGame && (
                          <div className="flex justify-between items-center text-xs">
                            <span className="text-slate-400">
                              Favorite Game:
                            </span>
                            <span className="text-white">
                              {miniPreviewStats.favoriteGame}
                            </span>
                          </div>
                        )}

                        <div className="pt-2">
                          <button
                            onClick={() => setShowStatsModal(true)}
                            className="w-full text-xs text-purple-400 hover:text-purple-300 transition-colors"
                          >
                            View Detailed Stats →
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Daily streak & achievements section */}
        <div className="mb-6">
          <DailyStreak days={streakDays} onClaim={handleClaimReward} />
        </div>

        {/* Horizontal scrolling leaderboard section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white">
              Community Leaderboard
            </h2>
            <button
              className="text-sm text-purple-400 hover:text-purple-300 flex items-center gap-1"
              onClick={() => setShowFullLeaderboard(true)}
            >
              View Full Rankings <ChevronRight size={16} />
            </button>
          </div>

          <div className="relative">
            <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-slate-900 to-transparent z-10 pointer-events-none"></div>
            <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-slate-900 to-transparent z-10 pointer-events-none"></div>

            <div className="overflow-x-auto py-2 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
              <div className="flex gap-3 min-w-max px-4">
                {/* Display actual leaderboard entries or empty placeholders */}
                {Array.from({ length: 10 }).map((_, index) =>
                  leaderboard[index] ? (
                    <LeaderboardEntry
                      key={leaderboard[index].id}
                      leader={leaderboard[index]}
                      rank={index}
                    />
                  ) : (
                    <LeaderboardEntry
                      key={`empty-${index}`}
                      rank={index}
                      isEmpty={true}
                    />
                  )
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Category selector */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-4">
            <h2 className="text-xl font-bold text-white">Game Categories</h2>
            <div className="text-xs py-1 px-2 bg-slate-800 rounded-full text-slate-400">
              {filteredGames.length} games available
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-2">
            <motion.div
              onClick={() => setActiveCategory("all")}
              className={`p-4 rounded-xl cursor-pointer transition-all border ${
                activeCategory === "all"
                  ? "bg-gradient-to-br from-slate-700 to-slate-800 border-slate-600"
                  : "bg-slate-800/50 border-slate-700 hover:border-slate-600"
              }`}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex flex-col items-center text-center">
                <div
                  className={`p-3 rounded-full ${
                    activeCategory === "all" ? "bg-white/10" : "bg-slate-700"
                  } mb-3`}
                >
                  <Sparkles
                    size={24}
                    className={
                      activeCategory === "all" ? "text-white" : "text-slate-400"
                    }
                  />
                </div>
                <h3
                  className={`font-medium ${
                    activeCategory === "all" ? "text-white" : "text-slate-300"
                  } mb-1`}
                >
                  All Games
                </h3>
                <p
                  className={`text-xs ${
                    activeCategory === "all"
                      ? "text-white/80"
                      : "text-slate-400"
                  }`}
                >
                  View everything
                </p>
              </div>
            </motion.div>

            {categories.map((category) => (
              <CategoryCard
                key={category.id}
                category={category}
                isActive={activeCategory === category.id}
                onClick={() => setActiveCategory(category.id)}
              />
            ))}
          </div>
        </div>

        {/* Games grid */}
        <div className="mb-12">
          {filteredGames.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {filteredGames.map((game) => (
                <GameCard
                  key={game.id}
                  game={game}
                  openMultiplayerLobby={openMultiplayerLobby}
                />
              ))}
            </div>
          ) : (
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 text-center">
              <Search className="text-slate-500 mx-auto mb-3" size={32} />
              <h3 className="text-lg font-medium text-white mb-2">
                No games found
              </h3>
              <p className="text-slate-400 mb-4">
                Try adjusting your search or category filters
              </p>
              <button
                onClick={() => {
                  setSearchQuery("");
                  setActiveCategory("all");
                }}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-white transition-colors"
              >
                Reset Filters
              </button>
            </div>
          )}
        </div>

        {/* Brain science explanation section */}
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-md rounded-xl border border-slate-700 overflow-hidden">
          <div className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row gap-6 md:gap-12">
              <div className="md:w-2/3">
                <h2 className="text-xl md:text-2xl font-bold text-white mb-4">
                  How Brain Training Works
                </h2>
                <div className="space-y-4 text-slate-300">
                  <p>
                    Your brain has incredible neuroplasticity—the ability to
                    rewire itself through consistent practice. These cognitive
                    exercises are modeled after validated lab tasks used in
                    neuroscience to assess and train core mental functions like
                    attention, memory, and flexibility.
                  </p>
                  <p>
                    Regular training can strengthen the prefrontal cortex and
                    improve communication between brain areas responsible for
                    decision-making, self-control, and emotion regulation,
                    functions that can be affected by substance use.
                  </p>
                  <p>
                    For best results, aim for 15-30 minutes of cognitive
                    training 3-5 times per week.
                  </p>
                </div>
              </div>

              <div className="md:w-1/3 relative">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-blue-500/20 rounded-xl blur-xl"></div>
                <div className="relative bg-slate-800/70 border border-slate-700 rounded-xl p-4">
                  <h3 className="text-white font-medium mb-3">
                    Recovery Benefits
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="p-1.5 bg-purple-500/20 rounded-lg">
                        <Shield size={16} className="text-purple-400" />
                      </div>
                      <div>
                        <h4 className="text-white text-sm font-medium">
                          Reduced Relapse Risk
                        </h4>
                        <p className="text-slate-400 text-xs">
                          Strengthened impulse control circuits
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="p-1.5 bg-blue-500/20 rounded-lg">
                        <Brain size={16} className="text-blue-400" />
                      </div>
                      <div>
                        <h4 className="text-white text-sm font-medium">
                          Cognitive Healing
                        </h4>
                        <p className="text-slate-400 text-xs">
                          Reverse substance-induced brain changes
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="p-1.5 bg-emerald-500/20 rounded-lg">
                        <Activity size={16} className="text-emerald-400" />
                      </div>
                      <div>
                        <h4 className="text-white text-sm font-medium">
                          Emotional Regulation
                        </h4>
                        <p className="text-slate-400 text-xs">
                          Better stress and emotion management
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="p-1.5 bg-amber-500/20 rounded-lg">
                        <Target size={16} className="text-amber-400" />
                      </div>
                      <div>
                        <h4 className="text-white text-sm font-medium">
                          Daily Functioning
                        </h4>
                        <p className="text-slate-400 text-xs">
                          Improved focus, memory, and productivity
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <AnimatePresence>
        {showFullLeaderboard && (
          <FullLeaderboardModal
            isOpen={showFullLeaderboard}
            onClose={() => setShowFullLeaderboard(false)}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showFullLeaderboard && (
          <FullLeaderboardModal
            isOpen={showFullLeaderboard}
            onClose={() => setShowFullLeaderboard(false)}
            leaderboard={leaderboard} // pass your leaderboard state here
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showPvPLobby && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex justify-center items-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, y: 10 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              className="bg-slate-900 rounded-xl shadow-xl p-6 w-full max-w-xl relative"
            >
              <button
                onClick={() => {
                  setShowPvPLobby(false);
                  setActiveMatch(null);
                  setSelectedGame(null);
                }}
                className="absolute top-3 right-3 text-slate-400 hover:text-white"
              >
                <X size={20} />
              </button>

              {activeMatch ? (
                // If we have an active match, show the MultiplayerGame component
                <MultiplayerGame
                  matchId={activeMatch}
                  gameComponent={getGameComponentByGameId(selectedGame)}
                  onExit={() => {
                    setShowPvPLobby(false);
                    setActiveMatch(null);
                    setSelectedGame(null);
                  }}
                />
              ) : (
                // Otherwise show the lobby to find players
                <PvPLobby
                  game={selectedPvPGame}
                  onMatchCreated={(matchId) => {
                    setActiveMatch(matchId);
                    setSelectedGame(selectedPvPGame);
                    // Don't close the modal - we'll show the multiplayer game there
                  }}
                  onLeaveLobby={() => {
                    setShowPvPLobby(false);
                    setSelectedPvPGame(null);
                  }}
                />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showStatsModal && (
          <PlayerStatsModal
            isOpen={showStatsModal}
            onClose={() => setShowStatsModal(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Gameroom;
